// The live adapter for Shot Map, Pass Map, Heat Map and Passing Profile (Track 2, T2-6 → T2-9): app-api's
// `/player-competitions`, `/player-current-league`, `/player-seasons`, `/player-shots` and/or
// `/player-passes`, `/matches` and `/match-goals` → the same shapes the snapshot gives those
// modules: her competitions, one competition's seasons, one season's events, and the match
// context the Opposition / Game State / Recipient chips read.
//
// Asked step by step, as the pickers need them (the snapshot held every season at once; with
// real data that's too much to fetch up front): her competitions once per widget, a
// competition's seasons when it's picked, a season's events when it's shown.

import type {
  MatchGoalsResponse,
  MatchSummary,
  MatchesResponse,
  PlayerCompetition,
  PlayerCompetitionsResponse,
  PlayerCurrentLeague,
  PlayerEvent,
  PlayerPassesResponse,
  PlayerSeasonsResponse,
  PlayerShotsResponse,
  ProtoEvent,
  ProtoFixture,
} from "@catbento/shared";
import type { CompetitionId } from "../data/league.js";
import { seasonSortKey } from "../data/league.js";
import type { PlayerId } from "../data/players.js";
import { passOf, type EventContext } from "../engines/event-map-kit.js";
import type { LiveApi } from "./api.js";
import { toProtoFixtures } from "./results.js";

/** Which events a map shows — the shell's `cfg.key`. */
export type EventKind = "shots" | "passes";

/** The event sets each module loads: Shot Map and Pass Map one each, Heat Map both. */
export const EVENT_KINDS = {
  shots: ["shots"],
  passes: ["passes"],
  both: ["shots", "passes"],
} as const satisfies Record<string, readonly EventKind[]>;

/** Her competitions (any she has events in: league, cups, Europe, national team — as the
 *  snapshot listed McCabe's FAWSL, UWCL and World Cup) and the one to open on. */
export interface EventCompetitions {
  /** Her current league first (the default), then the rest in the API's order. */
  competitions: PlayerCompetition[];
  /** Her current league when she has events there, else the first listed; null = none at all. */
  defaultId: CompetitionId | null;
}

export async function loadEventCompetitions(api: LiveApi, playerId: PlayerId): Promise<EventCompetitions> {
  const [{ competitions }, current] = await Promise.all([
    api.get<PlayerCompetitionsResponse>(`/player-competitions?playerId=${playerId}`),
    api.get<PlayerCurrentLeague>(`/player-current-league?playerId=${playerId}`),
  ]);
  // Opens on her league, as the snapshot opened McCabe on FAWSL (and the Stats tiles do, #229).
  const league = competitions.find((c) => c.id === current.competitionId);
  const ordered = league ? [league, ...competitions.filter((c) => c !== league)] : competitions;
  return { competitions: ordered, defaultId: ordered[0]?.id ?? null };
}

/**
 * The tile's stored competition + season, if they're this player's (`owner` = who they were
 * stored for). A new player starts on HIS league and current season (rules.md "Row 1": a new
 * player resets to her league) — not on the last player's league just because he once played
 * in it (Kane opened on Rice's Premier League, then on 22/23, his last season there). No owner
 * yet (a saved dashboard or preset from before it was stored) → the stored pair stands. Pure.
 */
export function storedForPlayer(
  owner: PlayerId | null | undefined,
  playerId: PlayerId | null,
  stored: { compId: string | null | undefined; seasonCode: string | null | undefined },
): { compId: string | null | undefined; seasonCode: string | null | undefined } {
  return owner != null && owner !== playerId ? { compId: null, seasonCode: null } : stored;
}

/** The competition to show: the stored one if she has it, else the default. */
export function resolveEventCompetition(ec: EventCompetitions, stored: string | null | undefined): CompetitionId | null {
  if (stored && ec.competitions.some((c) => String(c.id) === stored)) return Number(stored);
  return ec.defaultId;
}

/** Her seasons in one competition, newest first (the season picker's order). */
export async function loadEventSeasons(api: LiveApi, playerId: PlayerId, competitionId: CompetitionId): Promise<string[]> {
  const { seasons } = await api.get<PlayerSeasonsResponse>(
    `/player-seasons?playerId=${playerId}&competitionId=${competitionId}`,
  );
  return seasons.map((s) => String(s.id)).sort((a, b) => seasonSortKey(b) - seasonSortKey(a));
}

/** The season to show: the stored one if she has it, else her newest. Null = no seasons. */
export function resolveEventSeason(seasonCodes: string[], stored: string | null | undefined): string | null {
  if (stored && seasonCodes.includes(stored)) return stored;
  return seasonCodes[0] ?? null;
}

/** A value the snapshot's event always has; real data without it is a data problem, not
 *  something to plot at a made-up spot (fail fast). */
function need<T>(value: T | null, field: string, e: PlayerEvent): T {
  if (value === null) throw new Error(`${e.eventType ?? "Event"} ${e.id} (match ${e.matchId}) has no ${field}`);
  return value;
}

/**
 * One API event (a shot or a pass) → the snapshot's event shape (snake_case, the raw
 * StatsBomb blobs as they are). The snapshot's events came from old Catalyst's
 * `/api/playerShots` + `/api/playerPasses`, which read the same `golden.event` rows, so the
 * blobs match; only the top-level names differ.
 */
export function toProtoEvent(e: PlayerEvent): ProtoEvent {
  return {
    id: e.id,
    match_id: e.matchId,
    index: need(e.index, "index", e),
    match_date: e.matchDate,
    period: need(e.period, "period", e),
    minute: need(e.minute, "minute", e),
    second: need(e.second, "second", e),
    event_type: need(e.eventType, "event type", e),
    event_type_id: need(e.eventTypeId, "event type id", e),
    team_id: need(e.teamId, "team id", e),
    x: need(e.x, "x", e),
    y: need(e.y, "y", e),
    x_end: need(e.xEnd, "end x", e),
    y_end: need(e.yEnd, "end y", e),
    raw_event_attributes: e.rawEventAttributes as Record<string, unknown>,
    enriched_event_attributes: e.enrichedEventAttributes as Record<string, unknown>,
  };
}

/**
 * The goals in her matches → the Game State chip's timeline: each goal's side (home / away)
 * and minute. The API says whether a goal was her team's; whether her team was home comes
 * from her events' team vs the match's home team, so only her matches with events here get a
 * timeline — the only ones the chip asks about.
 */
export function toMatchGoals(
  goals: MatchGoalsResponse["goals"],
  events: ProtoEvent[],
  matches: MatchSummary[],
): EventContext["matchGoals"] {
  const byId = new Map(matches.map((m) => [m.matchId, m]));
  const homeByMatch = new Map<number, boolean>();
  for (const e of events) {
    const m = byId.get(e.match_id);
    // Not in this season's results: no opponent either, so the chip has nothing to ask.
    if (!m) continue;
    if (e.team_id !== m.homeTeamId && e.team_id !== m.awayTeamId) {
      throw new Error(`Event ${e.id}: team ${e.team_id} plays in neither side of match ${m.matchId}`);
    }
    homeByMatch.set(m.matchId, e.team_id === m.homeTeamId);
  }
  const out: EventContext["matchGoals"] = {};
  for (const matchId of homeByMatch.keys()) out[String(matchId)] = { goals: [] };
  for (const g of goals) {
    const isHome = homeByMatch.get(g.matchId);
    if (isHome === undefined) continue;
    if (g.minute === null) throw new Error(`Goal in match ${g.matchId} has no minute`);
    // Her team's goal is on her side; the other team's on the other.
    const team = g.isPlayerTeam === isHome ? "home" : "away";
    out[String(g.matchId)]!.goals.push({ team, minute: g.minute });
  }
  return out;
}

/**
 * Her passes' recipients by id, for the Recipient chip / tooltip / table (the snapshot looked
 * them up in its roster). StatsBomb spells one teammate differently from pass to pass
 * ("Alessia Russo" / "Alessia Mia Teresa Russo"), so each id keeps ONE name — the first seen —
 * and stays one chip option. The kit then shortens it (first + last word), as it did roster
 * names.
 */
export function recipientsById(passes: ProtoEvent[]): EventContext["playersById"] {
  const byId = new Map<string, { name: string }>();
  for (const p of passes) {
    const r = passOf(p).recipient;
    if (r && !byId.has(String(r.id))) byId.set(String(r.id), { name: r.name });
  }
  return byId;
}

/** One season's events plus the match context the chips, tooltips and table read. */
export interface LiveEventSeason {
  /** Empty when not asked for (the kinds the module loads). */
  shots: ProtoEvent[];
  passes: ProtoEvent[];
  /** Results (for "Opponent (H)" + the score) and goal timelines (Game State). */
  matchResults: Record<string, ProtoFixture[]>;
  matchGoals: EventContext["matchGoals"];
  /** Pass recipients' names (empty without passes — nothing else reads it). */
  playersById: EventContext["playersById"];
}

async function loadEvents(api: LiveApi, kind: EventKind, query: string): Promise<ProtoEvent[]> {
  const raw =
    kind === "shots"
      ? (await api.get<PlayerShotsResponse>(`/player-shots?${query}`)).shots
      : (await api.get<PlayerPassesResponse>(`/player-passes?${query}`)).passes;
  return raw.map(toProtoEvent);
}

/** One season's raw answers: her events (only the kinds asked for) plus the league's matches
 *  and the goals in her matches. Matches (T2-11) reads them too, alongside her appearances. */
export interface RawEventSeason {
  shots: ProtoEvent[];
  passes: ProtoEvent[];
  matches: MatchSummary[];
  goals: MatchGoalsResponse["goals"];
}

export async function fetchEventSeason(
  api: LiveApi,
  kinds: readonly EventKind[],
  playerId: PlayerId,
  competitionId: CompetitionId,
  seasonCode: string,
): Promise<RawEventSeason> {
  const q = `competitionId=${competitionId}&seasonId=${seasonCode}`;
  const none = Promise.resolve<ProtoEvent[]>([]);
  const [shots, passes, { matches }, { goals }] = await Promise.all([
    kinds.includes("shots") ? loadEvents(api, "shots", `playerId=${playerId}&${q}`) : none,
    kinds.includes("passes") ? loadEvents(api, "passes", `playerId=${playerId}&${q}`) : none,
    api.get<MatchesResponse>(`/matches?${q}`),
    api.get<MatchGoalsResponse>(`/match-goals?playerId=${playerId}&${q}`),
  ]);
  return { shots, passes, matches, goals };
}

export async function loadEventSeason(
  api: LiveApi,
  kinds: readonly EventKind[],
  playerId: PlayerId,
  competitionId: CompetitionId,
  seasonCode: string,
): Promise<LiveEventSeason> {
  const { shots, passes, matches, goals } = await fetchEventSeason(api, kinds, playerId, competitionId, seasonCode);
  return {
    shots,
    passes,
    matchResults: { [String(competitionId)]: toProtoFixtures(matches) },
    matchGoals: toMatchGoals(goals, [...shots, ...passes], matches),
    playersById: recipientsById(passes),
  };
}
